import React, { useMemo, useState } from "react";
import { SmallText, Spacer } from "../../components/core/utility";
import { AssignMaterialVehicleGrid } from "./AssignMaterialVehicleGrid";
import { Button } from "@vds/buttons";
import { Breadcrumbs, BreadcrumbItem } from "@vds/breadcrumbs";
import { OrderShippingSummaryAccordion } from "./OrderShippingSummaryAccordion";
import { redirectToPrevPage } from "./utils";

const getVehId = (vehicle) => {
  const stableId = vehicle?._stableId;
  const vehicleId = vehicle?.id || vehicle?.vehicle || vehicle?.vin || stableId;
  return vehicleId;
};

// Update header to accept disabled for Complete button
const ReviewRequestedItemsHeader = ({ onContinue, disabled }) => (
  <div style={{ margin: "1rem 0", width: "100%" }}>
    <div
      style={{
        fontFamily: "Verizon-NHG-eDS",
        fontWeight: 700,
        fontSize: "20px",
        lineHeight: "24px",
        color: "#000",
      }}
    >
      Review Requested Items
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "80px", // increased spacing between message and button
        marginTop: "8px",
      }}
    >
      <div
        style={{
          color: "#cc0000",
          fontFamily: "Verizon-NHG-eDS",
          fontSize: "11px",
          lineHeight: "16px",
          flex: 1,
        }}
      >
        <b>Please review</b> the items you have established for &quot;Return Only After Cancellation&quot; or &quot;Return and Reship&quot; before
        processing. After clicking &quot;Continue&quot;, this will generate a return ticket and you will not be able to cancel the return ticket. For
        reshipment, the line items will be available as soon as sales order has been generated for the return ticket.
      </div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Button size="small" variant="secondary" onClick={onContinue} aria-label="Complete" disabled={disabled}>
          Complete
        </Button>
      </div>
    </div>
  </div>
);

const buildSelectionIndex = (selectedVehicleEntities) => {
  const selectionIndex = new Map();
  if (!selectedVehicleEntities || typeof selectedVehicleEntities !== "object") return selectionIndex;
  Object.entries(selectedVehicleEntities).forEach(([materialKey, vehicleList]) => {
    (vehicleList || []).forEach((vehicle) => {
      const vehicleId = getVehId(vehicle);
      if (!vehicleId) return;
      if (!selectionIndex.has(vehicleId)) selectionIndex.set(vehicleId, new Set());
      selectionIndex.get(vehicleId).add(materialKey);
    });
  });
  return selectionIndex;
};

const mapBundleToGridData = (bundleId, groupsArray, selectionIndex) => {
  const gridGroups = Array.isArray(groupsArray) ? groupsArray : [];
  const vehiclesForGrid = [];
  gridGroups.forEach((group) => {
    (group?.vehicles || []).forEach((vehicle) => {
      const vehicleId = getVehId(vehicle);
      if (!vehicleId) return;
      const selectedKeysForVehicle = selectionIndex.get(vehicleId) || new Set();
      const allMappings = Array.isArray(vehicle.materialMapping) ? vehicle.materialMapping : [];
      const hasSelected = allMappings.some((mapping) => selectedKeysForVehicle.has(`${mapping?.hardwareId || ""}|${mapping?.subscriptionId || ""}`));
      if (!hasSelected) return;
      const normalizedMappings = allMappings.map((mapping) => ({
        ...mapping,
        _isSelected: selectedKeysForVehicle.has(`${mapping?.hardwareId || ""}|${mapping?.subscriptionId || ""}`),
        vinMaterials:
          Array.isArray(mapping?.vinMaterials) && mapping.vinMaterials.length
            ? mapping.vinMaterials
            : [{ materialName: mapping?.materialName || mapping?.hardwareName || mapping?.materialId || "Device" }],
      }));

      vehiclesForGrid.push({
        ...vehicle,
        id: vehicleId,
        materialMapping: normalizedMappings,
      });
    });
  });

  if (!vehiclesForGrid.length) return null;

  return {
    vehicleList: {
      [bundleId]: [
        {
          address: gridGroups?.[0]?.address || {},
          contact: gridGroups?.[0]?.contact || {},
          vehicles: vehiclesForGrid,
        },
      ],
    },
    externalSelectedVehicleIds: vehiclesForGrid.map((v) => v.id),
  };
};

const ReturnReshipSummaryComponent = ({ combinedVehiclesByBundle = {}, selectedVehicleEntities = {}, onContinue, onBack }) => {
  const selectionIndex = useMemo(() => buildSelectionIndex(selectedVehicleEntities), [selectedVehicleEntities]);
  const bundleEntries = useMemo(() => Object.entries(combinedVehiclesByBundle || {}), [combinedVehiclesByBundle]);

  const [disabledMap, setDisabledMap] = useState(new Set());

  const makeMapKey = (vehicle, mapping) => {
    const vehicleId = getVehId(vehicle);
    const hardwareId = mapping?.hardwareId || "";
    const subscriptionId = mapping?.subscriptionId || "";
    const workTicketId = (mapping?.workticketId || mapping?.workTicketId || "").toString();
    return `${vehicleId}|${hardwareId}|${subscriptionId}|${workTicketId}`;
  };

  const handleDeleteSelectedMapping = (vehicle, mapping) => {
    const key = makeMapKey(vehicle, mapping);
    setDisabledMap((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const hasAnyHighlighted = useMemo(() => {
    let anyHighlighted = false;
    bundleEntries.forEach(([, groups]) => {
      (groups || []).forEach((group) => {
        (group?.vehicles || []).forEach((vehicle) => {
          const vehicleId = getVehId(vehicle);
          const selectedKeysForVehicle = selectionIndex.get(vehicleId) || new Set();
          (vehicle?.materialMapping || []).forEach((mapping) => {
            if (anyHighlighted) return;
            const initiallySelected = selectedKeysForVehicle.has(`${mapping?.hardwareId || ""}|${mapping?.subscriptionId || ""}`);
            if (!initiallySelected) return;
            const key = makeMapKey(vehicle, mapping);
            if (!disabledMap.has(key)) anyHighlighted = true;
          });
        });
      });
    });
    return anyHighlighted;
  }, [bundleEntries, selectionIndex, disabledMap]);

  const dataForBundle = (bundleId, groups) => {
    const base = mapBundleToGridData(bundleId, groups, selectionIndex);
    if (!base) return null;
    const vehicleGroups = base.vehicleList[bundleId] || [];
    const transformedGroups = vehicleGroups.map((group) => {
      const updatedVehicles = (group.vehicles || []).map((vehicle) => {
        const updatedMappings = (vehicle.materialMapping || []).map((mapping) => {
          const isDisabled = disabledMap.has(makeMapKey(vehicle, mapping));
          return isDisabled ? { ...mapping, _isSelected: false } : mapping;
        });
        return { ...vehicle, materialMapping: updatedMappings };
      });
      return { ...group, vehicles: updatedVehicles };
    });
    return {
      vehicleList: { [bundleId]: transformedGroups },
      externalSelectedVehicleIds: transformedGroups.flatMap((group) => (group.vehicles || []).map((vehicle) => vehicle.id)),
    };
  };

  const handleComplete = () => {
    if (typeof onContinue === "function") {
      onContinue({ disabledKeys: Array.from(disabledMap) });
    }
  };

  const renderBreadcrumbs = () => (
    <>
      <Spacer top="10px" />
      <Breadcrumbs surface="light">
        <BreadcrumbItem key={0} active={false}>
          <span
            css={{ fontWeight: "400", color: "black", cursor: "pointer" }}
            onClick={() => {
              redirectToPrevPage();
            }}
          >
            Return / Reship
          </span>
        </BreadcrumbItem>
        <BreadcrumbItem key={1} active={false}>
          <span css={{ fontWeight: "700", color: "black" }}>Review Requested Items</span>
        </BreadcrumbItem>
      </Breadcrumbs>
      <Spacer bottom="1rem" />
      <OrderShippingSummaryAccordion />
    </>
  );

  return (
    <div>
      {renderBreadcrumbs()}
      <ReviewRequestedItemsHeader onContinue={handleComplete} disabled={!hasAnyHighlighted} />
      <div style={{ margin: "1rem 0", width: "100%" }}>
        {bundleEntries.length ? (
          bundleEntries.map(([bundleId, groups]) => {
            const data = dataForBundle(bundleId, groups);
            if (!data) return null;
            return (
              <div key={bundleId} style={{ marginBottom: "16px" }}>
                <Spacer top="6px" />
                <AssignMaterialVehicleGrid
                  bundleIds={[bundleId]}
                  addressId={""}
                  contactId={""}
                  crmRefId={""}
                  materialFilter={{}}
                  vehicleList={data.vehicleList}
                  activeBundleId={bundleId}
                  selectedMaterialDetails={null}
                  materialKey={"summary"}
                  externalSelectedVehicleIds={data.externalSelectedVehicleIds}
                  onSelectionChange={() => {}}
                  selectionLocked={true}
                  openAllAccordions={true}
                  selectionHighlight={true}
                  hideSelectionColumn={true}
                  showDeleteForSelected={true}
                  onDeleteSelectedMapping={handleDeleteSelectedMapping}
                />
              </div>
            );
          })
        ) : (
          <SmallText>No selected items</SmallText>
        )}
      </div>
    </div>
  );
};

export default ReturnReshipSummaryComponent;
